import React, { useRef, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { toast } from 'react-toastify';
import { Images, KeyPurpose, useModal } from 'utils';
import { UploadServices, uploadFile, uploadVideo } from 'store';
import { CreateChancel } from '../../buttons';
import { TextRow } from '../tableRows';
import { NotError } from '../../screens';
import { videoDefaultImages } from './constants';

const MAX_FILE_SIZE = 300 * 1024 * 1024;

// Trim a name for the localStorage in-progress marker — keeps the
// object small when many uploads are queued at once.

export const UploadVideo = ({ resourceId, model, afterSuccess }) => {
    const [error, setError] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [backgroundImage, setBackgroundImage] = useState(videoDefaultImages[0]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const { close } = useModal();
    const fileInputRef = useRef(null);
    const uploadingInProcess =
        (localStorage.getItem('uploadingInProcess') && JSON?.parse(localStorage.getItem('uploadingInProcess'))) || [];

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleUpload = (e) => {
        if (e?.size > MAX_FILE_SIZE) {
            toast(<NotError type={'error'} errMessage={'File size must be less than 300 MB.'} />);
            return;
        }

        error === 'currentFile' && setError('');

        if (e) {
            const pos = e.type.slice(e.type.search('/'));
            const fileType = pos.substring(1);
            const info = {
                file: e,
                name: e?.name,
                description: '',
                format: fileType,
            };
            setUploadedFiles([info, ...uploadedFiles]);
        }
    };

    const handleUploadImage = (file) => {
        if (!file) return;

        const pos = file.type.slice(file.type.search('/'));
        const fileType = pos.substring(1);

        const info = {
            file: file,
            name: file?.name,
            description: '',
            format: fileType,
            url: URL.createObjectURL(file),
        };

        setBackgroundImage(info);
        setUploadedImages((prev) => [info, ...prev]);
    };

    const handleSubmit = async () => {
        const checkFileName = uploadedFiles?.findIndex((file) => !file.name || file.name.trim() === '');

        if (!uploadedFiles?.length || checkFileName !== -1) {
            setError(checkFileName);
            return;
        }

        setUploadLoader(true);

        // New flow (post-eb-services retirement):
        //   1. Upload the background image (if the user chose one) via the
        //      generic presign flow.
        //   2. Upload each video directly to S3 via multipart presigned
        //      PUTs. Bytes NEVER pass through the api anymore.
        //   3. Once each video's S3 upload is complete, POST /videos/complete
        //      to persist the metadata + StoredFile on Goal.instructions[].
        //
        // Because the DB record is only created AFTER the S3 upload
        // finishes, "in-progress" no longer exists on the backend — the
        // in-flight state is entirely browser-side. The
        // `uploadingInProcess` localStorage entries are still tracked so
        // App.js can call VideoAbortMultiple after a browser crash to
        // clean up any records that DID reach the backend from a prior
        // successful upload that the user never confirmed.
        try {
            let backgroundStoredFile = null;
            const hasCustomBackground = !!backgroundImage?.file;
            if (hasCustomBackground) {
                backgroundStoredFile = await uploadFile(backgroundImage.file, KeyPurpose.CLINICAL_DOC);
            }

            // Show the "started" toast + close the modal early — video
            // uploads can take minutes and blocking the UI would be worse
            // than the small UX cost of losing the modal.
            toast(<NotError errMessage={'Video uploading was started.'} />);
            close();
            afterSuccess?.();

            for (let fileIndex = 0; fileIndex < uploadedFiles.length; fileIndex++) {
                const { file: videoFile, name, description } = uploadedFiles[fileIndex];

                let videoStoredFile;
                try {
                    videoStoredFile = await uploadVideo(videoFile, KeyPurpose.CLINICAL_VIDEO);
                } catch (uploadErr) {
                    // uploadVideo already aborted the S3 multipart internally.
                    // Nothing on the backend to clean up (no record was
                    // created yet — we only POST /complete on success).
                    continue;
                }

                // Persist the metadata now that the bytes are safely at S3.
                const backgroundForPayload = hasCustomBackground
                    ? backgroundStoredFile
                    // Static default image chosen from the palette — send
                    // the same shape the legacy backend accepted so the
                    // Goal.instructions[] embed schema is unchanged.
                    : backgroundImage;
                let metadataSaved = false;
                try {
                    const completeRes = await UploadServices.VideoComplete({
                        resourceId,
                        model,
                        file: videoStoredFile,
                        backgroundImage: backgroundForPayload,
                        defaultImage: !hasCustomBackground,
                        name,
                        description: description || '',
                    });
                    metadataSaved = true;
                    const videoId = completeRes?.data?.id;
                    if (videoId) {
                        const current =
                            (localStorage.getItem('uploadingInProcess') &&
                                JSON?.parse(localStorage.getItem('uploadingInProcess'))) ||
                            [];
                        const next = current.filter(
                            (item) => !(item?.resourceId === resourceId && item?.documentId === videoId),
                        );
                        if (next.length === 0) {
                            localStorage.removeItem('uploadingInProcess');
                            localStorage.removeItem('removeUnUploaded');
                        } else {
                            localStorage.setItem('uploadingInProcess', JSON.stringify(next));
                        }
                    }
                    afterSuccess?.();
                } catch (e) {
                    // Bytes are already in S3 but metadata save failed.
                    // Surface the actual error to the user so we don't
                    // silently show a "success" toast for a video that
                    // will never appear in the goal's video list. The
                    // orphan S3 object gets cleaned by lifecycle policy.
                    const errMsg =
                        e?.data?.message?.[0] ||
                        e?.data?.message ||
                        e?.response?.data?.message?.[0] ||
                        'Video upload failed to save.';
                    toast(<NotError type={'error'} errMessage={errMsg} />);
                }
                if (!metadataSaved) continue;
            }

            toast(<NotError errMessage={'Video uploaded and finalized successfully.'} />);
        } catch (err) {
            // toast(<NotError errMessage={'Failed to upload video.'} />);
        } finally {
            setUploadLoader(false);
        }
    };

    // const handleSubmit = async () => {
    //     const checkFileName = uploadedFiles?.findIndex((file) => !file.name || file.name.trim() === '');
    //
    //     if (!uploadedFiles?.length || checkFileName !== -1) {
    //         setError(checkFileName);
    //         return;
    //     }
    //
    //     setUploadLoader(true);
    //
    //     try {
    //
    //         let backgroundUploaded = null
    //         if(backgroundImage?.file){
    //             let formData = new FormData();
    //             formData.append('files', backgroundImage?.file);
    //             setUploadLoader(true);
    //             const res = await UploadServices.UploadImage(formData);
    //             backgroundUploaded = res?.data;
    //         }else{
    //             backgroundUploaded = backgroundImage
    //         }
    //
    //         for (let fileIndex = 0; fileIndex < uploadedFiles.length; fileIndex++) {
    //             const { file: videoFile, name, description } = uploadedFiles[fileIndex];
    //             const startParams = {
    //                 resourceId: resourceId,
    //                 model: model,
    //                 videoOriginalName: videoFile.name,
    //                 videoName: name,
    //                 videoDescription: description || '',
    //                 videoFileSize: videoFile.size,
    //                 videoMimetype: videoFile.type,
    //                 videoOriginPath: videoFile.name,
    //                 defaultImage: !backgroundImage?.file,
    //                 backgroundImage: backgroundUploaded,
    //             }
    //
    //             const createRes = await UploadServices.VideoStart(startParams);
    //             const videoId = createRes?.data?.id;
    //             if (!videoId) throw new Error('Failed to create video in backend');
    //
    //             const uploadParams = [
    //                 ...uploadingInProcess,
    //                 {
    //                     resourceId: resourceId,
    //                     model: model,
    //                     documentId: videoId,
    //                 }
    //             ]
    //
    //             localStorage.setItem('uploadingInProcess', JSON.stringify(uploadParams));
    //
    //             // Step 2: Split and upload chunks
    //             afterSuccess?.()
    //             const totalChunks = Math.ceil(videoFile.size / CHUNK_SIZE);
    //
    //             toast(<NotError errMessage={'Video uploading was started.'} />);
    //             close()
    //
    //             for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    //                 const start = chunkIndex * CHUNK_SIZE;
    //                 const end = Math.min(start + CHUNK_SIZE, videoFile.size);
    //                 const chunk = videoFile.slice(start, end);
    //
    //                 const formData = new FormData();
    //                 formData.append('resourceId', resourceId);
    //                 formData.append('model', model);
    //                 formData.append('documentId', videoId);
    //                 formData.append('file', chunk, videoFile.name);
    //
    //                 const partRes = await UploadServices.VideoPart(formData);
    //                 afterSuccess?.()
    //                 if (partRes.status < 200 || partRes.status >= 300) {
    //                     throw new Error(`Failed to upload chunk ${chunkIndex + 1} of ${totalChunks}`);
    //                 }
    //             }
    //
    //             // Step 3: Finalize upload
    //             const completeRes = await UploadServices.VideoComplete({
    //                 resourceId: resourceId,
    //                 model: model,
    //                 documentId: videoId,
    //             });
    //
    //             // const updatedUploading = localStorage.getItem('uploadingInProcess') && JSON?.parse(localStorage.getItem('uploadingInProcess')) || [];
    //             const filteredUploading = uploadingInProcess?.filter((item) => (item?.resourceId !== resourceId && item?.documentId !== videoId));
    //             localStorage.setItem('uploadingInProcess', JSON.stringify(filteredUploading));
    //
    //             if(filteredUploading?.length === 0){
    //                 localStorage.removeItem('uploadingInProcess');
    //                 localStorage.removeItem('removeUnUploaded');
    //             }
    //
    //             afterSuccess?.();
    //             if (completeRes.status < 200 || completeRes.status >= 300) {
    //                 throw new Error('Failed to finalize video upload');
    //             }
    //         }
    //
    //         close();
    //         toast(<NotError errMessage={'Video uploaded and finalized successfully.'} />);
    //     } catch (err) {
    //         // toast(<NotError errMessage={'Failed to upload video.'} />);
    //     } finally {
    //         setUploadLoader(false);
    //     }
    // };

    const renderSize = (item) => {
        const bytes = item?.size;
        const KB = 1024;
        const MB = KB * 1024;

        if (bytes >= MB) {
            return (bytes / MB).toFixed(2) + ' MB';
        } else if (bytes >= KB) {
            return (bytes / KB).toFixed(2) + ' KB';
        } else {
            return bytes + ' bytes';
        }
    };

    const removeFile = (item, key) => {
        const updatedItems = uploadedFiles?.filter((item, k) => k !== key);
        setUploadedFiles(updatedItems);
    };

    const changeFileInfo = (e, index) => {
        if (error === index) setError('');

        setUploadedFiles((prev) => prev.map((file, i) => (i === index ? { ...file, [e.target.name]: e.target.value } : file)));
    };

    return (
        <div style={{ width: 400 }}>
            <div>
                <p className="modal-header-title">{'Upload Instructional Video'}</p>
                <p className="select-document-desc">{'Upload an instructional video that demonstrates how to implement this goal.'}</p>
            </div>

            <div>
                {uploadedFiles?.length === 0 && (
                    <div style={uploadedFiles?.length > 0 ? {} : { border: 'none' }} className="upload-file-header-box">
                        <FileUploader handleChange={handleUpload} name="file" types={['MP4', 'MOV', 'AVI', 'MKV', 'WEBM']}>
                            <div className="drag-drop-component">
                                <div className="upload-dock-icon">
                                    <img src={Images.uploadDockCloud} alt="upload" style={{ margin: 0 }} />
                                </div>
                                <label className="custom-file-upload">
                                    <input
                                        onClick={(event) => (event.target.value = null)}
                                        onChange={handleUpload}
                                        type="file"
                                        accept="video/*"
                                        id="file"
                                    />
                                    <i className="fa fa-cloud-upload" />
                                    <div className="upload">
                                        <p>
                                            <span style={{ fontWeight: 600 }}>Drag & drop</span>
                                            <span style={{ margin: '0 4px' }}>your video here or</span>
                                            <span style={{ color: '#2E6DD8', fontWeight: 600 }}>click to upload</span>
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </FileUploader>
                    </div>
                )}

                {!!uploadedFiles?.length > 0 && (
                    <div className="uploaded-files-wrapper" style={{ maxHeight: 450 }}>
                        {uploadedFiles.map((i, j) => (
                            <div
                                className="loaded-documents-box"
                                key={j}
                                style={{
                                    gap: 0,
                                    margin: '0 0 8px 0',
                                    flexDirection: 'column',
                                    borderColor: error === j ? '#FE7070' : '#E4E7EC',
                                }}
                            >
                                <div className="uploaded-file-info-header">
                                    <div className="space-between">
                                        <div className="flex-align-center" style={{ gap: 8, width: '90%' }}>
                                            <img src={Images.uploadedVideo} alt="icon" />
                                            <p className="loaded-document-name full-width">
                                                <TextRow name={i?.name} fontWeight={600} textWidth={10} />
                                            </p>
                                        </div>
                                        <button className="reset-btn-by-default" onClick={() => removeFile(i, j)}>
                                            <img src={Images.redTrashLine} alt="icon" />
                                        </button>
                                    </div>
                                    <div className="uploaded-file-detail-box" style={{ marginLeft: 32 }}>
                                        <p>{renderSize(i?.file)}</p>
                                        <div className="loaded-circle" />
                                        {(i?.file?.type || i?.file?.format) && (
                                            <p>{(i?.file?.type || i?.file?.format)?.replace('image/', '')}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="full-width">
                                    <div>
                                        <div className="choose-or-upload-image">
                                            <p>Choose or upload an image to use as the video cover</p>
                                        </div>
                                        <div className="choose-or-upload-image-actions">
                                            <FileUploader
                                                handleChange={handleUploadImage}
                                                name="file"
                                                types={['JPG', 'JPEG', 'PNG', 'WEBP']}
                                            >
                                                <div className="drag-drop-video-background-image">
                                                    <label className="custom-file-upload">
                                                        <input
                                                            ref={fileInputRef}
                                                            onClick={(event) => (event.target.value = null)}
                                                            type="file"
                                                            accept="image/*"
                                                            id="file"
                                                        />
                                                        <i className="fa fa-cloud-upload" />
                                                        <div className="upload">
                                                            <p>
                                                                <span style={{ color: '#2E6DD8', fontWeight: 600 }}>
                                                                    Upload Image, Drag & drop
                                                                </span>
                                                                <span style={{ margin: '0 4px' }}>or Select from Frames</span>
                                                                <span>JPG, PNG, WebP (Max size: 5 MB)</span>
                                                            </p>
                                                        </div>
                                                    </label>
                                                </div>
                                            </FileUploader>

                                            <div className="video-default-images-wrapper">
                                                {videoDefaultImages?.map((i, j) => (
                                                    <div
                                                        key={j}
                                                        onClick={() => setBackgroundImage(i)}
                                                        style={
                                                            !backgroundImage?.file && backgroundImage?.url === i?.url
                                                                ? { border: '3px solid #0C66E4' }
                                                                : {}
                                                        }
                                                        className="video-default-image"
                                                    >
                                                        <img src={i?.url} alt="url" />
                                                    </div>
                                                ))}

                                                {!!uploadedImages?.length &&
                                                    uploadedImages?.map((i, j) => (
                                                        <div style={{ position: 'relative' }}>
                                                            <button
                                                                onClick={() => {
                                                                    const filteredImages = uploadedImages?.filter(
                                                                        (img, index) => index !== j
                                                                    );
                                                                    setUploadedImages(filteredImages);
                                                                    if (backgroundImage?.file && backgroundImage?.url === i?.url) {
                                                                        setBackgroundImage(videoDefaultImages[0]);
                                                                    }
                                                                }}
                                                                className="remove-uploaded-image"
                                                            >
                                                                <img src={Images.remove} alt="removeImage" />
                                                            </button>

                                                            <div
                                                                key={j}
                                                                onClick={() => setBackgroundImage(i)}
                                                                style={
                                                                    backgroundImage?.file && backgroundImage?.url === i?.url
                                                                        ? { border: '3px solid #0C66E4' }
                                                                        : {}
                                                                }
                                                                className="video-default-image"
                                                            >
                                                                <img
                                                                    src={i?.url || (i?.file ? URL.createObjectURL(i.file) : '')}
                                                                    alt="preview"
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}

                                                <div>
                                                    <button
                                                        type="button"
                                                        className="add-back-image-btn"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            openFileDialog();
                                                        }}
                                                    >
                                                        <img src={Images.plus} alt="add" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="uploaded-file-name-input-wrapper">
                                        <p>File name*</p>
                                        <input
                                            onChange={(e) => changeFileInfo(e, j)}
                                            value={i?.name}
                                            type="text"
                                            name={'name'}
                                            placeholder={'File Name*'}
                                            maxLength={50}
                                        />
                                    </div>
                                    <div className="uploaded-file-desc-input-wrapper">
                                        <textarea
                                            onChange={(e) => changeFileInfo(e, j)}
                                            value={i?.description}
                                            name={'description'}
                                            placeholder={'File Description'}
                                            maxLength={100}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <CreateChancel
                loader={uploadLoader}
                create={'Upload Video'}
                chancel={'Cancel'}
                onCreate={handleSubmit}
                onClose={close}
                buttonWidth="223px"
                createButnMargin={'16px'}
                disabled={!uploadedFiles?.length}
            />
        </div>
    );
};
